import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Upload, FileText, Loader2, CheckCircle, AlertCircle, Trash2,
  Settings, Download, Image as ImageIcon, Sparkles, X, Eye, Mail, Search,
} from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";

const CATEGORIES = [
  ["epicerie", "Épicerie"],
  ["restaurant", "Restaurant"],
  ["essence", "Essence"],
  ["equipement_dojo", "Équipement dojo"],
  ["fournitures_bureau", "Fournitures bureau"],
  ["telecom", "Télécom"],
  ["publicite_marketing", "Publicité / Marketing"],
  ["loyer", "Loyer"],
  ["electricite", "Électricité"],
  ["internet", "Internet"],
  ["assurance", "Assurance"],
  ["transport", "Transport"],
  ["voyage_hebergement", "Voyage / Hébergement"],
  ["formation", "Formation"],
  ["don_charite", "Don / Charité"],
  ["soins_sante", "Soins santé"],
  ["vetements", "Vêtements"],
  ["loisirs", "Loisirs"],
  ["autre", "Autre"],
];

const CAT_LABEL = Object.fromEntries(CATEGORIES);

const COMPANIES = [
  { key: "perso", label: "Perso", color: "bg-blue-500" },
  { key: "impact", label: "Institut Impact", color: "bg-impact-red" },
  { key: "rdjp", label: "Fondation RDJP", color: "bg-emerald-600" },
];

export default function BookkeepingModule() {
  const [receipts, setReceipts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showGmailIngest, setShowGmailIngest] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [reviewCount, setReviewCount] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const load = useCallback(async () => {
    setLoading(true);
    const qs = filterStatus === "review"
      ? "?needs_review=true"
      : filterStatus ? `?status=${filterStatus}` : "";
    try {
      const [r, s, rq] = await Promise.all([
        api.get(`/bookkeeping/receipts${qs}`),
        api.get("/bookkeeping/summary"),
        api.get("/bookkeeping/review-queue"),
      ]);
      setReceipts(r.data);
      setSummary(s.data);
      setReviewCount(rq.data.count || 0);
    } catch {
      toast.error("Erreur chargement");
    } finally { setLoading(false); }
  }, [filterStatus]);

  const validateReceipt = async (id) => {
    try {
      await api.post(`/bookkeeping/receipts/${id}/validate`);
      toast.success("Reçu validé ✓");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    try {
      const r = await api.post("/bookkeeping/receipts/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`📤 ${r.data.uploaded} factures uploadées · Clique "Traiter" pour extraire`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const processPending = async () => {
    setProcessing(true);
    try {
      const r = await api.post("/bookkeeping/receipts/process-pending?limit=20");
      toast.success(`🤖 ${r.data.processed} factures extraites · ${r.data.remaining} restant`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur traitement");
    } finally { setProcessing(false); }
  };

  const updateReceipt = async (id, patch) => {
    try {
      const r = await api.patch(`/bookkeeping/receipts/${id}`, patch);
      setReceipts(receipts.map((x) => (x.id === id ? r.data : x)));
      load();
      toast.success("Mis à jour");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const deleteReceipt = async (id) => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    await api.delete(`/bookkeeping/receipts/${id}`);
    load();
    toast.success("Supprimé");
  };

  const exportCsv = async (company) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/bookkeeping/export.csv${company ? `?company=${company}` : ""}`;
    const r = await api.get(`/bookkeeping/export.csv${company ? `?company=${company}` : ""}`, { responseType: "blob" });
    const blob = new Blob([r.data], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `bookkeeping-${company || "all"}.csv`;
    link.click();
    toast.success("CSV téléchargé");
  };

  const pendingCount = receipts.filter((r) => r.status === "uploaded").length;
  const errorCount = receipts.filter((r) => r.status === "error").length;
  const needsAmountCount = receipts.filter((r) => r.status === "needs_manual_amount").length;

  return (
    <div data-testid="bookkeeping-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Comptabilité"
          breadcrumbs={[{ label: "Finance & administration" }, { label: "Comptabilité" }]}
          subtitle="Upload factures → extraction auto par IA → répartition Perso / Impact / RDJP selon tes règles → export CSV pour le comptable."
          actions={
            <>
              <label className={`bg-impact-red hover:bg-foreground text-white px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 cursor-pointer ${uploading ? "opacity-50" : ""}`} data-testid="upload-btn">
                {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                {uploading ? "Upload…" : "Uploader factures"}
                <input type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" onChange={handleUpload} disabled={uploading} className="hidden" />
              </label>
              <button onClick={() => setShowGmailIngest(true)} data-testid="gmail-ingest-btn" className="border border-zinc-700 px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 hover:bg-zinc-800 text-zinc-200">
                <Mail size={14} /> Importer Gmail
              </button>
              {pendingCount > 0 && (
                <button onClick={processPending} disabled={processing} data-testid="process-btn" className="bg-foreground hover:bg-impact-gold hover:text-foreground text-white px-5 py-2.5 rounded-lg text-sm flex items-center gap-2">
                  {processing ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
                  {processing ? "Extraction IA…" : `🤖 Traiter ${pendingCount} en attente`}
                </button>
              )}
              <button onClick={() => setShowRules(true)} data-testid="rules-btn" className="border border-zinc-700 px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 hover:bg-zinc-800 text-zinc-200">
                <Settings size={14} /> Règles répartition
              </button>
            </>
          }
        />
      </div>

      <div className="px-8 lg:px-12 py-8 space-y-6">
        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="hard-border p-4 bg-secondary/40">
              <div className="ticker text-muted-foreground text-xs">Total factures</div>
              <div className="text-3xl font-display font-black mt-1">{summary.receipt_count}</div>
            </div>
            {COMPANIES.map((c) => (
              <div key={c.key} className={`hard-border p-4 text-white ${c.color}`}>
                <div className="ticker text-white/80 text-xs">{c.label}</div>
                <div className="text-2xl font-mono font-black mt-1">{(summary.totals[c.key] || 0).toFixed(2)} $</div>
                <button onClick={() => exportCsv(c.key)} data-testid={`export-${c.key}`} className="mt-2 text-[10px] uppercase tracking-wider hover:underline flex items-center gap-1">
                  <Download size={10} /> CSV {c.label}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Filters + global export */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-muted-foreground">Filtrer :</span>
          {["", "review", "uploaded", "extracted", "needs_manual_amount", "error"].map((s) => (
            <button key={s || "all"} onClick={() => setFilterStatus(s)} data-testid={`filter-${s || "all"}`} className={`px-3 py-1 text-xs ticker hard-border ${filterStatus === s ? "bg-foreground text-white" : "hover:bg-secondary"} ${s === "review" && reviewCount > 0 && filterStatus !== "review" ? "border-amber-500 text-amber-700" : ""}`}>
              {s === "" ? "TOUTES" : s === "review" ? `⚠️ À VÉRIFIER (${reviewCount})` : s === "uploaded" ? `EN ATTENTE (${pendingCount})` : s === "extracted" ? "EXTRAITES" : s === "needs_manual_amount" ? `À COMPLÉTER (${needsAmountCount})` : `ERREURS (${errorCount})`}
            </button>
          ))}
          <div className="flex-1" />
          {selected.size > 0 && (
            <button onClick={() => setShowEmailDialog(true)} data-testid="email-receipts-btn" className="text-xs ticker bg-impact-red text-white px-3 py-1 hover:bg-foreground flex items-center gap-1 transition-colors">
              ✉️ Envoyer par courriel ({selected.size})
            </button>
          )}
          <button onClick={() => exportCsv()} data-testid="export-all" className="text-xs ticker hard-border px-3 py-1 hover:bg-secondary flex items-center gap-1">
            <Download size={12} /> Tout exporter (CSV)
          </button>
        </div>

        {/* Receipts table */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            <Loader2 className="animate-spin mx-auto mb-2" size={24} />
            Chargement…
          </div>
        ) : receipts.length === 0 ? (
          <div className="hard-border p-12 text-center text-muted-foreground">
            <FileText size={48} className="mx-auto mb-3 opacity-30" />
            <p className="mb-1">Aucune facture pour le moment.</p>
            <p className="text-xs">Clique <strong>Uploader factures</strong> pour démarrer (JPEG / PNG / WEBP, max 10 MB).</p>
          </div>
        ) : (
          <div className="hard-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-foreground text-white">
                <tr>
                  <th className="px-2 py-2 w-8"></th>
                  <th className="px-3 py-2 text-left">Statut</th>
                  <th className="px-3 py-2 text-left">Confiance</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Commerçant</th>
                  <th className="px-3 py-2 text-left">Catégorie</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-center">Perso</th>
                  <th className="px-3 py-2 text-center">Impact</th>
                  <th className="px-3 py-2 text-center">RDJP</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {receipts.map((r) => (
                  <ReceiptRow key={r.id} r={r} onUpdate={updateReceipt} onDelete={deleteReceipt} onView={() => setViewReceipt(r)} onValidate={validateReceipt} selected={selected.has(r.id)} onToggleSelect={toggleSelect} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showRules && <RulesEditor onClose={() => { setShowRules(false); load(); }} />}
      {showGmailIngest && <GmailIngestDialog onClose={() => { setShowGmailIngest(false); load(); }} />}
      {viewReceipt && <ReceiptViewer receipt={viewReceipt} onClose={() => setViewReceipt(null)} />}
      {showEmailDialog && (
        <EmailReceiptsDialog
          receipts={receipts.filter((r) => selected.has(r.id))}
          onClose={() => setShowEmailDialog(false)}
          onSent={() => { setShowEmailDialog(false); setSelected(new Set()); }}
        />
      )}
    </div>
  );
}

function EmailReceiptsDialog({ receipts, onClose, onSent }) {
  const withFile = receipts.filter((r) => r.storage_path);
  const noFile = receipts.filter((r) => !r.storage_path);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("Factures justificatives — Institut Impact Élite Martial");
  const lines = receipts.map((r, i) => `${i + 1}. ${r.merchant || "Reçu"} — ${(r.total_amount || 0).toFixed(2)} $ (${r.receipt_date || "date n/d"})`).join("\n");
  const [body, setBody] = useState(
    `Bonjour,\n\nVous trouverez ci-joint les factures justificatives suivantes :\n\n${lines}\n\nN'hésitez pas si vous avez besoin d'une précision.\n\nMerci et bonne journée,\nRalph D. Jean-Pierre\nImpact Kick-Boxing inc. — Institut Impact Élite Martial`
  );
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!to.trim()) { toast.error("Entre une adresse courriel"); return; }
    if (withFile.length === 0) { toast.error("Aucun fichier joignable parmi la sélection"); return; }
    setSending(true);
    try {
      const r = await api.post("/bookkeeping/email-receipts", {
        to: to.trim(), subject, body, receipt_ids: receipts.map((x) => x.id),
      });
      toast.success(`✅ Brouillon Gmail créé — ${r.data.attached.length} facture(s) jointe(s)${r.data.missing.length ? ` · ${r.data.missing.length} sans fichier` : ""}. Relis et envoie depuis Gmail.`);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création brouillon");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white hard-border max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="email-receipts-dialog">
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="ticker text-impact-gold text-xs">ENVOYER PAR COURRIEL</div>
            <h2 className="font-display font-bold text-xl">Factures → Brouillon Gmail</h2>
          </div>
          <button onClick={onClose} className="p-1"><X size={20} /></button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Crée un <strong>brouillon Gmail</strong> avec les factures jointes (jamais d'envoi automatique). Tu relis et envoies depuis Gmail.</p>
        <label className="ticker text-muted-foreground text-xs">Destinataire</label>
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="exemple@courriel.com" className="hard-border px-3 py-2 w-full mb-3 mt-1" data-testid="email-to-input" />
        <label className="ticker text-muted-foreground text-xs">Objet</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} className="hard-border px-3 py-2 w-full mb-3 mt-1" data-testid="email-subject-input" />
        <label className="ticker text-muted-foreground text-xs">Message</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="hard-border px-3 py-2 w-full mb-3 mt-1 text-sm" data-testid="email-body-input" />
        <div className="hard-border p-3 mb-3 bg-secondary/20 text-xs">
          <div className="font-semibold mb-1">📎 {withFile.length} fichier(s) joint(s)</div>
          {noFile.length > 0 && (
            <div className="text-amber-700">⚠️ {noFile.length} reçu(s) sans fichier (non joints) : {noFile.map((r) => r.merchant || "?").join(", ")}</div>
          )}
        </div>
        <button onClick={submit} disabled={sending} data-testid="email-send-btn" className="bg-impact-red hover:bg-foreground text-white px-4 py-2.5 ticker w-full flex items-center justify-center gap-2 transition-colors">
          {sending ? <Loader2 className="animate-spin" size={14} /> : <>✉️ Créer le brouillon</>}
        </button>
      </div>
    </div>
  );
}

function ReceiptRow({ r, onUpdate, onDelete, onView, onValidate, selected, onToggleSelect }) {
  const statusIcon = {
    uploaded: <span className="text-xs text-muted-foreground">⏳ En attente</span>,
    extracting: <span className="text-xs text-impact-gold flex items-center gap-1"><Loader2 className="animate-spin" size={10} /> Extraction</span>,
    extracted: <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle size={12} /> Extraite</span>,
    reviewed: <span className="text-xs text-blue-700 flex items-center gap-1"><CheckCircle size={12} /> Validée</span>,
    needs_manual_amount: <span className="text-xs text-amber-700 flex items-center gap-1"><AlertCircle size={12} /> Montant à entrer</span>,
    error: <span className="text-xs text-impact-red flex items-center gap-1"><AlertCircle size={12} /> Erreur</span>,
  }[r.status] || r.status;

  const total = r.total_amount || 0;
  const alloc = r.allocation || {};
  const cs = r.confidence_score;
  const confColor = cs == null ? "text-muted-foreground" : cs >= 85 ? "text-emerald-700" : cs >= 70 ? "text-amber-600" : "text-impact-red";

  return (
    <tr className={`border-b hover:bg-secondary/20 ${r.needs_review ? "bg-amber-50" : ""}`} data-testid={`receipt-row-${r.id}`}>
      <td className="px-2 py-2 text-center">
        <input
          type="checkbox"
          checked={!!selected}
          disabled={!r.storage_path}
          onChange={() => onToggleSelect(r.id)}
          title={r.storage_path ? "Sélectionner pour envoi" : "Aucun fichier joignable"}
          data-testid={`receipt-select-${r.id}`}
        />
      </td>
      <td className="px-3 py-2">
        {statusIcon}
        {r.source === "gmail" && <span className="ml-1 text-[9px] uppercase tracking-wider text-blue-600" title="Importé depuis Gmail">✉ Gmail</span>}
      </td>
      <td className="px-3 py-2" data-testid={`receipt-confidence-${r.id}`}>
        {cs == null ? <span className="text-xs text-muted-foreground">—</span> : (
          <span className={`text-xs font-mono font-bold ${confColor}`} title={(r.validations || []).map((v) => `${v.status === "fail" ? "✗" : v.status === "warn" ? "⚠" : "✓"} ${v.message}`).join("\n")}>
            {cs}%{r.needs_review && <span className="ml-1" title="À vérifier">⚠️</span>}
          </span>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-xs">{r.receipt_date || "—"}</td>
      <td className="px-3 py-2 max-w-[180px] truncate" title={r.merchant}>{r.merchant || <span className="text-muted-foreground italic">—</span>}</td>
      <td className="px-3 py-2">
        {r.category ? (
          <select value={r.category} onChange={(e) => onUpdate(r.id, { category: e.target.value })} className="hard-border px-2 py-0.5 text-xs">
            {CATEGORIES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        ) : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-3 py-2 text-right font-mono">{total > 0 ? `${total.toFixed(2)} $` : "—"}</td>
      <td className="px-3 py-2 text-center text-xs">{alloc.perso ?? "—"}%</td>
      <td className="px-3 py-2 text-center text-xs">{alloc.impact ?? "—"}%</td>
      <td className="px-3 py-2 text-center text-xs">{alloc.rdjp ?? "—"}%</td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {r.needs_review && (
          <button onClick={() => onValidate(r.id)} title="Valider (approuver)" className="p-1 hover:text-emerald-700" data-testid={`receipt-validate-${r.id}`}><CheckCircle size={14} /></button>
        )}
        <button onClick={onView} title="Voir image" className="p-1 hover:text-impact-gold"><Eye size={14} /></button>
        <button onClick={() => onDelete(r.id)} title="Supprimer" className="p-1 hover:text-impact-red"><Trash2 size={14} /></button>
      </td>
    </tr>
  );
}

function ReceiptViewer({ receipt, onClose }) {
  const imgUrl = `${process.env.REACT_APP_BACKEND_URL}/api/bookkeeping/receipts/${receipt.id}/image`;
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white hard-border max-w-3xl w-full p-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="ticker text-impact-gold text-xs">FACTURE</div>
            <h2 className="font-display font-bold text-xl">{receipt.merchant || receipt.filename}</h2>
            <p className="text-xs text-muted-foreground">{receipt.receipt_date}</p>
          </div>
          <button onClick={onClose} className="p-1"><X size={20} /></button>
        </div>
        <img src={imgUrl} alt={receipt.filename} className="w-full max-h-[60vh] object-contain border" />
        <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
          <div><strong>Total :</strong> {receipt.total_amount?.toFixed(2)} $</div>
          <div><strong>Sous-total :</strong> {receipt.subtotal?.toFixed(2)} $</div>
          <div><strong>TPS :</strong> {receipt.tax_gst?.toFixed(2)} $</div>
          <div><strong>TVQ :</strong> {receipt.tax_qst?.toFixed(2)} $</div>
          <div><strong>Catégorie :</strong> {CAT_LABEL[receipt.category] || receipt.category}</div>
          <div><strong>Répartition :</strong> {receipt.allocation ? Object.entries(receipt.allocation).map(([k, v]) => `${k}=${v}%`).join(" · ") : "—"}</div>
        </div>
        {receipt.validations?.length > 0 && (
          <div className="mt-3 hard-border p-3 bg-secondary/20" data-testid="receipt-validations">
            <div className="ticker text-xs text-muted-foreground mb-2 flex items-center justify-between">
              <span>CONTRÔLES DE VALIDATION</span>
              {receipt.confidence_score != null && (
                <span className={`font-mono font-bold ${receipt.confidence_score >= 85 ? "text-emerald-700" : receipt.confidence_score >= 70 ? "text-amber-600" : "text-impact-red"}`}>
                  Confiance {receipt.confidence_score}%
                </span>
              )}
            </div>
            <ul className="space-y-1 text-xs">
              {receipt.validations.map((v, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span>{v.status === "fail" ? "❌" : v.status === "warn" ? "⚠️" : "✅"}</span>
                  <span className={v.status === "fail" ? "text-impact-red" : v.status === "warn" ? "text-amber-700" : "text-muted-foreground"}>{v.message}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {receipt.extraction_error && (
          <div className="mt-3 p-2 bg-red-50 text-impact-red text-xs">
            <strong>Erreur extraction :</strong> {receipt.extraction_error}
          </div>
        )}
      </div>
    </div>
  );
}

function RulesEditor({ onClose }) {
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/bookkeeping/allocation-rules").then((r) => {
      setRules(r.data.rules);
      setLoading(false);
    });
  }, []);

  const update = (cat, company, value) => {
    const v = Math.max(0, Math.min(100, parseInt(value) || 0));
    setRules({ ...rules, [cat]: { ...rules[cat], [company]: v } });
  };

  const save = async () => {
    // Validate sums
    for (const [cat, alloc] of Object.entries(rules)) {
      const s = Object.values(alloc).reduce((a, b) => a + (b || 0), 0);
      if (s !== 100) {
        toast.error(`${CAT_LABEL[cat] || cat}: somme = ${s}% (doit être 100%)`);
        return;
      }
    }
    setSaving(true);
    try {
      await api.put("/bookkeeping/allocation-rules", { rules });
      toast.success("Règles sauvegardées");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center"><div className="bg-white p-6 hard-border">Chargement…</div></div>;
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white hard-border max-w-4xl w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="ticker text-impact-gold text-xs">— RÈGLES DE RÉPARTITION</div>
            <h2 className="font-display font-bold text-2xl">Comptabilité automatique</h2>
            <p className="text-sm text-muted-foreground">Pour chaque catégorie, définis le % alloué à Perso, Institut Impact, et Fondation RDJP. Total = 100%.</p>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="hard-border overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-foreground text-white sticky top-0">
              <tr><th className="px-3 py-2 text-left">Catégorie</th><th className="px-3 py-2">Perso %</th><th className="px-3 py-2">Impact %</th><th className="px-3 py-2">RDJP %</th><th className="px-3 py-2">Total</th></tr>
            </thead>
            <tbody>
              {CATEGORIES.map(([key, label]) => {
                const a = rules[key] || { perso: 0, impact: 0, rdjp: 0 };
                const sum = (a.perso || 0) + (a.impact || 0) + (a.rdjp || 0);
                return (
                  <tr key={key} className="border-b">
                    <td className="px-3 py-1.5">{label}</td>
                    {["perso", "impact", "rdjp"].map((c) => (
                      <td key={c} className="px-2 py-1.5 text-center">
                        <input type="number" min="0" max="100" value={a[c] || 0} onChange={(e) => update(key, c, e.target.value)} className="w-16 hard-border px-1 py-0.5 text-center" data-testid={`rule-${key}-${c}`} />
                      </td>
                    ))}
                    <td className={`px-3 py-1.5 text-center font-mono ${sum === 100 ? "text-emerald-700" : "text-impact-red"}`}>{sum}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="hard-border px-4 py-2 ticker hover:bg-secondary">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="save-rules" className="bg-impact-red hover:bg-foreground text-white px-5 py-2 ticker disabled:opacity-50">
            {saving ? "Enregistrement…" : "Sauvegarder"}
          </button>
        </div>
      </div>
    </div>
  );
}

function GmailIngestDialog({ onClose }) {
  const [query, setQuery] = useState("");
  const [scanning, setScanning] = useState(false);
  const [importing, setImporting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [scanned, setScanned] = useState(false);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.get(`/bookkeeping/gmail-ingest/scan${query ? `?query=${encodeURIComponent(query)}` : ""}`);
      setMessages(r.data.messages || []);
      setScanned(true);
      // Pré-sélectionner ceux pas encore importés
      const preset = new Set((r.data.messages || []).filter((m) => !m.already_imported).map((m) => m.id));
      setSelected(preset);
      toast.success(`📬 ${r.data.messages?.length || 0} emails trouvés (filtre: ${r.data.query})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur scan Gmail (vérifie connexion dans Intégrations)");
    } finally { setScanning(false); }
  };

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const importSelected = async () => {
    if (selected.size === 0) { toast.error("Sélectionne au moins un email"); return; }
    setImporting(true);
    try {
      const r = await api.post("/bookkeeping/gmail-ingest/import", { message_ids: Array.from(selected) });
      toast.success(`📥 ${r.data.imported} reçus importés · ${r.data.skipped_duplicates} doublons ignorés · ${r.data.errors?.length || 0} erreurs`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur import");
    } finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="gmail-ingest-dialog">
      <div className="bg-background hard-border max-w-4xl w-full max-h-[90vh] flex flex-col p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="ticker text-impact-gold text-xs">— INGESTION GMAIL</div>
            <h2 className="font-display font-bold text-2xl flex items-center gap-2"><Mail size={22} /> Importer reçus depuis Gmail</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Scanne ton Gmail pour les emails avec pièces jointes (PDF / image) ressemblant à des factures.
              Sélectionne ceux à importer dans la queue de comptabilité.
            </p>
          </div>
          <button onClick={onClose} data-testid="close-gmail-ingest"><X size={20} /></button>
        </div>

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="Filtre Gmail (défaut: factures / reçus 90 derniers jours)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 hard-border px-3 py-2 text-sm"
            data-testid="gmail-query-input"
          />
          <button onClick={scan} disabled={scanning} data-testid="gmail-scan-btn" className="bg-impact-red hover:bg-foreground text-white px-5 py-2 ticker flex items-center gap-2 disabled:opacity-50">
            {scanning ? <Loader2 className="animate-spin" size={14} /> : <Search size={14} />}
            {scanning ? "Scan…" : "Scanner"}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto hard-border">
          {!scanned ? (
            <div className="p-12 text-center text-muted-foreground text-sm">
              Clique <strong>Scanner</strong> pour rechercher des reçus dans Gmail.
            </div>
          ) : messages.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground text-sm">
              Aucun email trouvé. Essaie une requête plus large (ex: <code>from:stripe</code>).
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-foreground text-white sticky top-0">
                <tr>
                  <th className="px-2 py-2 w-8">
                    <input type="checkbox"
                      checked={selected.size > 0 && messages.every((m) => selected.has(m.id) || m.already_imported)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelected(new Set(messages.filter((m) => !m.already_imported).map((m) => m.id)));
                        } else { setSelected(new Set()); }
                      }}
                      data-testid="gmail-select-all"
                    />
                  </th>
                  <th className="px-2 py-2 text-left">De</th>
                  <th className="px-2 py-2 text-left">Sujet</th>
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-center">PJ</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {messages.map((m) => (
                  <tr key={m.id} className={`border-b hover:bg-secondary/30 ${m.already_imported ? "opacity-50" : ""}`} data-testid={`gmail-msg-${m.id}`}>
                    <td className="px-2 py-2 text-center">
                      <input type="checkbox"
                        checked={selected.has(m.id)}
                        disabled={m.already_imported}
                        onChange={() => toggle(m.id)}
                        data-testid={`gmail-msg-check-${m.id}`}
                      />
                    </td>
                    <td className="px-2 py-2 max-w-[160px] truncate text-xs" title={m.from}>{m.from}</td>
                    <td className="px-2 py-2 max-w-[260px] truncate" title={m.subject}>{m.subject}</td>
                    <td className="px-2 py-2 text-xs text-muted-foreground whitespace-nowrap">{m.date?.slice(0, 16)}</td>
                    <td className="px-2 py-2 text-center font-mono">{m.num_attachments}</td>
                    <td className="px-2 py-2 text-xs">
                      {m.already_imported && <span className="text-emerald-700 flex items-center gap-1"><CheckCircle size={12} /> Importé</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-between items-center mt-4">
          <span className="text-sm text-muted-foreground">{selected.size} email(s) sélectionné(s)</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="hard-border px-4 py-2 ticker hover:bg-secondary">Annuler</button>
            <button onClick={importSelected} disabled={importing || selected.size === 0} data-testid="gmail-import-btn" className="bg-impact-red hover:bg-foreground text-white px-5 py-2 ticker disabled:opacity-50 flex items-center gap-2">
              {importing ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
              {importing ? "Import…" : `Importer ${selected.size}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

